// Team editor modal: the rows of Case.team, edited against the firm's
// staff list and saved whole through PUT /api/cases/:id/team. A row the
// sync wrote by name alone goes back out as it came unless replaced.
// Mounted only while open, so the draft is born from the matter each time.

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CaseTeamMember, CaseTeamRole } from "@duralex/shared";
import { Dialog } from "../chrome/Dialog";
import { useFirmStaff } from "../../hooks/useFirmStaff";
import type { FirmStaff } from "../../api/firmStaff";
import { updateCaseTeam, type MatterWithClient } from "../../api/mock";
import { TEAM_ROLE_LABEL } from "../../lib/caseLabels";

const ROLES = Object.keys(TEAM_ROLE_LABEL) as CaseTeamRole[];
const REMOVE = String.fromCharCode(215);
const MIDDOT = String.fromCharCode(183);

interface Props {
  matter: MatterWithClient;
  onClose: () => void;
  /** The Case `_id` the PUT route is keyed on. */
  matterDocId: string;
}

// The role a picked account implies, when it implies one: a paralegal
// left in the attorney role would sign the forms (caseToFormContext).
function rowFor(s: FirmStaff, role: CaseTeamRole): CaseTeamMember {
  const row: CaseTeamMember = {
    userId: s.id,
    role: s.role === "attorney" || s.role === "paralegal" ? s.role : role,
    name: s.fullName,
  };
  // A placeholder account's address is made up and would reach the client.
  if (s.email && !s.isPlaceholder) row.email = s.email;
  return row;
}

export function TeamAssignmentEditor({ matter, onClose, matterDocId }: Props) {
  const { data: staff, isPending: staffPending, isError: staffFailed } = useFirmStaff();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<CaseTeamMember[]>(matter.team ?? []);

  const mutation = useMutation({
    mutationFn: (team: CaseTeamMember[]) => updateCaseTeam(matterDocId, { team }),
    onSuccess: (res) => {
      queryClient.setQueryData<MatterWithClient | null>(["matter", matter.eID], (prev) =>
        prev ? { ...prev, team: res.team } : prev,
      );
      queryClient.invalidateQueries({ queryKey: ["matter", matter.eID] });
      onClose();
    },
  });
  const staffById = useMemo(() => {
    const map = new Map<string, FirmStaff>();
    for (const s of staff ?? []) map.set(s.id, s);
    return map;
  }, [staff]);

  const patch = (i: number, next: (row: CaseTeamMember) => CaseTeamMember) =>
    setRows((all) => all.map((row, j) => (j === i ? next(row) : row)));

  const handleSave = () => mutation.mutate(rows.filter((r) => r.userId || r.name));

  const options = staff ?? [];

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Manage team - ${matter.eID}`}
      locked={mutation.isPending}
    >
      {rows.map((row, i) => {
        const known = row.userId ? staffById.has(row.userId) : false;
        return (
          <div key={i} className="team-modal-row">
            <select
              aria-label="Person"
              value={row.userId ?? ""}
              onChange={(e) => {
                const s = staffById.get(e.target.value);
                if (s) patch(i, (r) => rowFor(s, r.role));
              }}
            >
              {!known && (
                <option value={row.userId ?? ""}>{row.name || "- Pick a person -"}</option>
              )}
              {options.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.fullName} {MIDDOT} {s.role}
                </option>
              ))}
            </select>
            <select
              aria-label="Role"
              value={row.role}
              onChange={(e) => patch(i, (r) => ({ ...r, role: e.target.value as CaseTeamRole }))}
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {TEAM_ROLE_LABEL[role]}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="remove"
              aria-label={`Remove ${row.name || "row"}`}
              onClick={() => setRows((all) => all.filter((_, j) => j !== i))}
            >
              {REMOVE}
            </button>
          </div>
        );
      })}
      {rows.length === 0 && <p className="team-modal-empty">No one is on this matter yet.</p>}
      {staffPending && <p className="team-modal-empty">Loading staff...</p>}
      {staffFailed && (
        <div role="alert" className="cc-form-error">
          Could not load the firm's staff - the list is unavailable.
        </div>
      )}
      <button
        type="button"
        className="team-modal-add"
        disabled={!staff}
        // "other" so that a person left in the default role never signs
        // the forms (caseToFormContext takes the first attorney row).
        onClick={() => setRows((all) => [...all, { role: "other", name: "" }])}
      >
        Add person
      </button>
      {mutation.isError && (
        <div role="alert" className="cc-form-error">
          {mutation.error.message}
        </div>
      )}
      <div className="team-modal-foot">
        <button type="button" onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </button>
        <button
          type="button"
          className="primary"
          onClick={handleSave}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Saving..." : "Save"}
        </button>
      </div>
    </Dialog>
  );
}
